import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { dateToLocalDateString, dateToPeriodLabel } from './weeklyReports';
import {
    endOfWeekFromStart,
    extractHasMoreOlder,
    mergeById,
    normalizeDataset,
    oldestPeriodStartAmong,
    parsePaymentDate,
    startOfWeekContaining,
    stripTime,
} from './expenses';
import { priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { can } from '../../supplementary_components/userDataDelegate';
import { LoadingIcon } from '../../supplementary_components/loading_icon';
import listStyles from '../../general_components/content_management/list_view/listView.module.scss';
import reportStyles from './reports.module.scss';
import reportListStyles from './payableInvoicesList.module.scss';
import moneyCodeListStyles from './moneyCodeList.module.scss';
import { ThreeDotsButton } from '../../general_components/content_management/reusableItems/buttons';
import { MdHistory } from 'react-icons/md';
import { openActivitiesPopup } from '../../general_components/activitiesPopup/activitiesPopup';
import activitiesStyles from '../../general_components/activitiesPopup/activitiesPopup.module.scss';
import { openAttachmentsPopup } from '../../general_components/attachmentsPopup/attachmentsPopup';
import { entityHasAttachments } from '../../general_components/attachmentsPopup/supplementaryFilesListing';
import { MONEY_CODE_ATTACHMENTS_POPUP_KIND } from '../../general_components/attachmentsPopup/moneyCodeAttachmentsPopup';
import { PAYABLE_INVOICE_ATTACHMENTS_POPUP_KIND } from './payableInvoiceAttachmentsPopup';
import { ATTACHMENT_ENTITY_TYPES } from '../../general_components/attachmentsPopup/attachmentEntityConfig';
import { ListDescriptionCell } from '../../general_components/descriptionPopup/descriptionPopup';
import { PopupOnClick } from '../../general_components/content_management/reusableItems/itemOnPopup';
import { addPopup, addNotification } from '../../supplementary_components/store';
import { Form } from '../../general_components/forms/Form';
import { ConfirmationDialogue } from '../../general_components/forms/ConfirmationDialogue';
import { buildIssueMoneyCodeForm } from './issueMoneyCodeForm';
import { buildEditMoneyCodeForm } from './editMoneyCodeForm';

const MONEY_CODE_GRID_COLUMNS = '12rem 0.5fr 0.7fr 0.5fr 1fr 1.6fr 11rem 10rem 5rem';
const MONEY_CODE_ACTIVITIES_POPUP_KIND = 'moneyCodeActivities';
const MONEY_CODE_DESCRIPTION_POPUP_KIND = 'moneyCodeDescription';

const PURPOSES_WITHOUT_ATTACHMENTS = new Set(['CashAdvance']);

const FormattedCell = styled.div`
    ${(props) => props.$conditionalFormatting || ''}
`;

function userCanManageMoneyCode() {
    return can('MONEY_CODES', 'MANAGE');
}

function formatEnumLabel(value) {
    if (!value) return '-';
    return String(value).replace(/([a-z])([A-Z])/g, '$1 $2');
}

function moneyCodeStatusConditionalFormatting(status) {
    switch (status) {
        case 'NotUsed':
            return '& > span { color: var(--inProgressYellow); font-weight: bold; }';
        case 'Used':
            return '& > span { color: var(--main-color); font-weight: bold; }';
        case 'Void':
            return '& > span { color: var(--light-gray); font-weight: bold; }';
        default:
            return null;
    }
}

function purposeRequiresReimbursement(purpose) {
    return purpose === 'CashAdvance' || purpose === 'LumperFee';
}

function formatInvoiceReferenceEquipment(invoice) {
    const type = invoice?.equipmentType;
    if (type === 'Truck' && invoice?.truck?.truckNumber) {
        return `Truck ${invoice.truck.truckNumber}`;
    }
    if (type === 'Trailer' && invoice?.trailer?.trailerNumber) {
        return `Trailer ${invoice.trailer.trailerNumber}`;
    }
    if (invoice?.truck?.truckNumber) {
        return `Truck ${invoice.truck.truckNumber}`;
    }
    if (invoice?.trailer?.trailerNumber) {
        return `Trailer ${invoice.trailer.trailerNumber}`;
    }
    return null;
}

function formatMoneyCodeDisplayDescription(row) {
    if (row?.purpose === 'Invoice') {
        return row?.invoice?.description ?? null;
    }
    return row?.description ?? null;
}

function formatMoneyCodeReference(row) {
    switch (row?.purpose) {
        case 'Invoice': {
            if (row?.invoice?.invoiceNumber == null) {
                return '-';
            }
            const base = `Invoice#: ${row.invoice.invoiceNumber}`;
            const equipment = formatInvoiceReferenceEquipment(row.invoice);
            return equipment ? `${base} (${equipment})` : base;
        }
        case 'CashAdvance': {
            const d = row?.driver;
            if (!d) return '-';
            const name = [d.firstName, d.lastName].filter(Boolean).join(' ').trim();
            return name || '-';
        }
        case 'Fuel':
        case 'Scale':
        case 'Accessories':
            return row?.truck?.truckNumber ?? '-';
        case 'Parking':
            if (row?.trailer?.trailerNumber) {
                return `Trailer: ${row.trailer.trailerNumber}`;
            }
            if (row?.truck?.truckNumber) {
                return `Truck: ${row.truck.truckNumber}`;
            }
            return '-';
        case 'LumperFee':
            return row?.load?.loadNumber ?? '-';
        default:
            return '-';
    }
}

function formatPartialUsageSuffix(row) {
    const amount = row?.amount;
    const used = row?.usedAmount;
    if (amount == null || used == null || used >= amount) {
        return '';
    }
    return ` (${priceOnFinish(used)}/${priceOnFinish(amount)})`;
}

function isMoneyCodeVoid(moneyCode) {
    return moneyCode?.status === 'Void';
}

function sumNonVoidMoneyCodeAmounts(moneyCodes) {
    return (moneyCodes ?? []).reduce((sum, moneyCode) => {
        if (isMoneyCodeVoid(moneyCode)) {
            return sum;
        }
        const amount = Number(moneyCode?.amount);
        return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0);
}

function formatPeriodLabelWhenNotCurrentYear(weekStart, weekEnd) {
    const opts = { month: 'long', day: 'numeric', year: 'numeric' };
    return `${weekStart.toLocaleDateString('en-US', opts)} - ${weekEnd.toLocaleDateString('en-US', opts)}`;
}

function formatMoneyCodePeriodLabel(group) {
    const currentYear = new Date().getFullYear();
    const { weekStart, weekEnd } = group;
    const outsideCurrentYear =
        weekStart.getFullYear() !== currentYear || weekEnd.getFullYear() !== currentYear;
    const label = outsideCurrentYear
        ? formatPeriodLabelWhenNotCurrentYear(weekStart, weekEnd)
        : dateToPeriodLabel(weekStart, false, 'weekly', weekEnd);
    const total = sumNonVoidMoneyCodeAmounts(group.moneyCodes);
    return `${label} (${priceOnFinish(total)})`;
}

function canVoidMoneyCode(moneyCode) {
    if (isMoneyCodeVoid(moneyCode)) return false;
    const amount = moneyCode?.amount;
    const used = moneyCode?.usedAmount ?? 0;
    if (amount == null) return moneyCode?.status === 'NotUsed';
    if (moneyCode?.status === 'NotUsed') return true;
    return used < amount;
}

function openEditMoneyCode(moneyCode, onUpdated) {
    addPopup({
        content: Form,
        popupData: { configuration: buildEditMoneyCodeForm(moneyCode) },
        postSuccessAction: (value) => onUpdated(value),
        EZCloseAllowed: false,
    });
}

function syncMoneyCodeFromEfs(moneyCode, onUpdated) {
    return new Promise((resolve) => {
        sendRequest(
            'POST',
            `${API_ROOT}moneycodes/${moneyCode.id}/sync-efs-status`,
            null,
            (payload) => {
                onUpdated(payload);
                addNotification({ message: 'Money code synced from EFS', type: 'success' });
                resolve();
            },
            (err) => {
                addNotification({
                    message: err?.customError ? err.customError : 'Sync failed',
                    type: 'alert',
                });
                resolve();
            },
            null,
        );
    });
}

function confirmVoidMoneyCode(moneyCode, onUpdated) {
    addPopup({
        content: ConfirmationDialogue,
        popupData: {
            action: (postResultAction) => {
                sendRequest(
                    'POST',
                    `${API_ROOT}moneycodes/${moneyCode.id}/void`,
                    null,
                    (payload) => {
                        onUpdated(payload);
                        addNotification({ message: 'Money code voided', type: 'success' });
                        postResultAction(true);
                    },
                    (err) => {
                        addNotification({
                            message: err?.customError ? err.customError : 'Void failed',
                            type: 'alert',
                        });
                        postResultAction(false);
                    },
                    null,
                );
            },
            name: 'voidMoneyCodeConfirm',
            formTitle: 'Void Money Code',
            submitTitle: 'Void',
            method: 'POST',
            message: `Are you sure you want to void this money code for ${priceOnFinish(moneyCode.amount)}?`,
        },
        postSuccessAction: () => {},
        EZCloseAllowed: true,
    });
}

function MoneyCodeStatusCell({ row }) {
    const statusLabel = formatEnumLabel(row?.status);
    const partialSuffix = row?.status === 'Used' ? formatPartialUsageSuffix(row) : '';

    return (
        <div className={moneyCodeListStyles.statusBlock}>
            <FormattedCell $conditionalFormatting={moneyCodeStatusConditionalFormatting(row?.status)}>
                <span>
                    {statusLabel}
                    {partialSuffix}
                </span>
            </FormattedCell>
            <MoneyCodeReimbursementLine purpose={row?.purpose} reimbursed={row?.reimbursed} status={row?.status} />
        </div>
    );
}

function MoneyCodeReimbursementLine({ purpose, reimbursed, status }) {
    if (
        !purposeRequiresReimbursement(purpose) ||
        reimbursed === 'Void' ||
        reimbursed == null ||
        status === 'Void'
    ) {
        return null;
    }
    if (reimbursed === 'Reimbursed') {
        return (
            <div
                className={`${moneyCodeListStyles.reimbursementLine} ${moneyCodeListStyles.reimbursementReimbursed}`}
            >
                (Reimbursed)
            </div>
        );
    }
    if (reimbursed === 'NotReimbursed') {
        return (
            <div
                className={`${moneyCodeListStyles.reimbursementLine} ${moneyCodeListStyles.reimbursementNotReimbursed}`}
            >
                (Not Reimbursed)
            </div>
        );
    }
    return null;
}

function MoneyCodeAttachmentsCell({ moneyCode, canEdit, onMoneyCodeUpdated }) {
    if (PURPOSES_WITHOUT_ATTACHMENTS.has(moneyCode?.purpose)) {
        return <span className={moneyCodeListStyles.attachmentsDash}>-</span>;
    }

    const isInvoicePurpose = moneyCode?.purpose === 'Invoice';
    const invoice = isInvoicePurpose ? moneyCode?.invoice : null;

    if (isInvoicePurpose && !invoice?.id) {
        return <span className={moneyCodeListStyles.attachmentsDash}>-</span>;
    }

    const attachmentFiles = isInvoicePurpose ? invoice?.files : moneyCode?.attachments;
    const uploaded = entityHasAttachments(attachmentFiles);

    const titleLabel = isInvoicePurpose
        ? invoice?.invoiceNumber != null
            ? String(invoice.invoiceNumber)
            : ''
        : moneyCode?.efsCode != null && String(moneyCode.efsCode).trim() !== ''
          ? String(moneyCode.efsCode)
          : moneyCode?.id != null
            ? String(moneyCode.id)
            : '';

    const openPopup = (e) => {
        e.stopPropagation();
        if (isInvoicePurpose) {
            openAttachmentsPopup({
                entityType: ATTACHMENT_ENTITY_TYPES.PAYABLE_INVOICE,
                entity: invoice,
                titleLabel,
                canEdit,
                onEntityUpdated: (updatedInvoice) => {
                    if (!updatedInvoice || !onMoneyCodeUpdated) return;
                    onMoneyCodeUpdated({
                        ...moneyCode,
                        invoice: { ...invoice, ...updatedInvoice },
                    });
                },
                popupKind: PAYABLE_INVOICE_ATTACHMENTS_POPUP_KIND,
            });
            return;
        }

        openAttachmentsPopup({
            entityType: ATTACHMENT_ENTITY_TYPES.MONEY_CODE,
            entity: moneyCode,
            titleLabel,
            canEdit,
            onEntityUpdated: onMoneyCodeUpdated,
            popupKind: MONEY_CODE_ATTACHMENTS_POPUP_KIND,
        });
    };

    if (!uploaded) {
        if (isMoneyCodeVoid(moneyCode)) {
            return <span className={moneyCodeListStyles.attachmentsDash}>-</span>;
        }
        return (
            <button type="button" className={reportListStyles.paperworkMissingButton} onClick={openPopup}>
                Missing
            </button>
        );
    }

    return (
        <button type="button" className={reportListStyles.seeAttachmentsButton} onClick={openPopup}>
            See attachments
        </button>
    );
}

function MoneyCodeReferenceCell({ row, companyData }) {
    const text = formatMoneyCodeReference(row);
    if (row?.purpose === 'Invoice' && row?.invoice?.id != null) {
        return (
            <PopupOnClick
                text={text}
                itemType="payableInvoice"
                itemId={row.invoice.id}
                companyData={companyData}
                alwaysUnderlined
            />
        );
    }
    return <span>{text}</span>;
}

function buildDisplayColumns({ userCanEdit, onMoneyCodeUpdated, companyData }) {
    return [
        {
            label: 'Purpose',
            columnName: 'purpose',
            valueInterpreter: (val) => formatEnumLabel(val),
        },
        {
            label: 'Status',
            columnName: 'status',
            render: (row) => <MoneyCodeStatusCell row={row} />,
        },
        {
            label: 'EFS Code',
            columnName: 'efsCode',
            valueInterpreter: (val) => (val != null && String(val).trim() !== '' ? String(val) : '-'),
        },
        {
            label: 'Amount',
            columnName: 'amount',
            valueInterpreter: (val) => (val != null ? priceOnFinish(val) : '-'),
        },
        {
            label: 'Reference',
            columnName: 'reference',
            render: (row) => <MoneyCodeReferenceCell row={row} companyData={companyData} />,
        },
       
        {
            label: 'Description',
            columnName: 'description',
            render: (row) => (
                <ListDescriptionCell
                    description={formatMoneyCodeDisplayDescription(row)}
                    titleLabel={
                        row.efsCode != null && String(row.efsCode).trim() !== ''
                            ? String(row.efsCode)
                            : row.id != null
                              ? String(row.id)
                              : ''
                    }
                    titlePrefix={
                        row?.purpose === 'Invoice' ? 'Description — Invoice' : 'Description — Money Code'
                    }
                    popupKind={MONEY_CODE_DESCRIPTION_POPUP_KIND}
                />
            ),
        },
        {
            label: 'Attachments',
            columnName: 'attachments',
            render: (row) => (
                <MoneyCodeAttachmentsCell
                    moneyCode={row}
                    canEdit={userCanEdit}
                    onMoneyCodeUpdated={onMoneyCodeUpdated}
                />
            ),
        },
        {
            label: 'Latest Actions',
            columnName: 'latestActions',
            render: (row) => (
                <div
                    className={activitiesStyles.latestActionsContainer}
                    onClick={(e) => {
                        e.stopPropagation();
                        openActivitiesPopup({
                            targetType: 'MONEY_CODE',
                            objectId: row.id,
                            titleLabel:
                                row.efsCode != null && String(row.efsCode).trim() !== ''
                                    ? String(row.efsCode)
                                    : row.id != null
                                      ? String(row.id)
                                      : '',
                            popupKind: MONEY_CODE_ACTIVITIES_POPUP_KIND,
                        });
                    }}
                >
                    <MdHistory />
                </div>
            ),
        },
    ];
}

function groupMoneyCodesByWeek(moneyCodes, firstDayOfAWeek) {
    const buckets = new Map();
    for (const moneyCode of moneyCodes) {
        let weekStart;
        let weekEnd;
        if (moneyCode.period?.startDate && moneyCode.period?.endDate) {
            weekStart = parsePaymentDate(moneyCode.period.startDate);
            weekEnd = parsePaymentDate(moneyCode.period.endDate);
        } else {
            const codeDate = parsePaymentDate(moneyCode.issuedAt);
            if (!codeDate) continue;
            weekStart = startOfWeekContaining(codeDate, firstDayOfAWeek);
            weekEnd = endOfWeekFromStart(weekStart);
        }
        if (!weekStart) continue;
        const key = dateToLocalDateString(weekStart);
        if (!buckets.has(key)) {
            buckets.set(key, {
                weekStart,
                weekEnd,
                moneyCodes: [],
            });
        }
        buckets.get(key).moneyCodes.push(moneyCode);
    }
    const groups = [...buckets.values()].sort((a, b) => b.weekStart.getTime() - a.weekStart.getTime());
    for (const g of groups) {
        g.moneyCodes.sort((a, b) => {
            const byIssuedAt = String(b.issuedAt || '').localeCompare(String(a.issuedAt || ''));
            if (byIssuedAt !== 0) return byIssuedAt;
            return String(b.id ?? '').localeCompare(String(a.id ?? ''));
        });
    }
    return groups;
}

function renderMoneyCodeColumnCell(col, moneyCode) {
    if (col.render) {
        return col.render(moneyCode);
    }
    const raw = col.columnName === 'reference' ? moneyCode : moneyCode[col.columnName];
    const text = col.valueInterpreter ? col.valueInterpreter(raw, moneyCode) : raw;
    const formatting =
        typeof col.conditionalFormatting === 'function' ? col.conditionalFormatting(raw) : null;
    const content = <span>{text}</span>;
    if (formatting) {
        return <FormattedCell $conditionalFormatting={formatting}>{content}</FormattedCell>;
    }
    return content;
}

function fetchMoneyCodesResponse(queryString, signal) {
    const qs = queryString || '?pageNumber=0';
    return new Promise((resolve) => {
        sendRequest(
            'GET',
            `${API_ROOT}moneycodes${qs}`,
            signal,
            (res) => resolve(res ?? {}),
            () => resolve({ dataset: [], hasMoreOlder: false }),
            null,
        );
    });
}

function mergeMoneyCodeIntoList(prev, moneyCode) {
    if (!moneyCode || moneyCode.id == null) return prev;
    const idStr = String(moneyCode.id);
    const idx = prev.findIndex((row) => String(row.id) === idStr);
    if (idx >= 0) {
        const next = [...prev];
        const merged = { ...next[idx], ...moneyCode };
        if (
            (moneyCode.attachments == null || moneyCode.attachments === undefined) &&
            next[idx].attachments != null
        ) {
            merged.attachments = next[idx].attachments;
        }
        if (moneyCode.invoice && next[idx].invoice) {
            merged.invoice = { ...next[idx].invoice, ...moneyCode.invoice };
            if (
                (moneyCode.invoice.files == null || moneyCode.invoice.files === undefined) &&
                next[idx].invoice.files != null
            ) {
                merged.invoice.files = next[idx].invoice.files;
            }
        }
        next[idx] = merged;
        return next;
    }
    return [moneyCode, ...prev];
}

export const EfsMoneyCodeManagement = ({ companyData }) => {
    const firstDayOfAWeek = companyData?.firstDayOfAWeek || 'monday';

    const [allMoneyCodes, setAllMoneyCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadMoreBusy, setLoadMoreBusy] = useState(false);
    const [error, setError] = useState(null);
    const [hasMoreOlder, setHasMoreOlder] = useState(true);
    const [oldestLoadedPeriodStart, setOldestLoadedPeriodStart] = useState(null);

    const userCanEdit = userCanManageMoneyCode();

    const applyMoneyCodeFromApi = useCallback((apiValue) => {
        if (apiValue && apiValue.id != null) {
            setAllMoneyCodes((prev) => mergeMoneyCodeIntoList(prev, apiValue));
        }
    }, []);

    const displayColumns = useMemo(
        () => buildDisplayColumns({ userCanEdit, onMoneyCodeUpdated: applyMoneyCodeFromApi, companyData }),
        [userCanEdit, applyMoneyCodeFromApi, companyData],
    );

    const openIssueMoneyCode = useCallback(() => {
        addPopup({
            content: Form,
            popupData: {
                configuration: buildIssueMoneyCodeForm({ onIssued: applyMoneyCodeFromApi }),
            },
            postSuccessAction: (value) => applyMoneyCodeFromApi(value),
            EZCloseAllowed: false,
        });
    }, [applyMoneyCodeFromApi]);

    useEffect(() => {
        let cancelled = false;
        const ac = new AbortController();

        const run = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetchMoneyCodesResponse('?pageNumber=0', ac.signal);
                if (cancelled) return;
                const rows = normalizeDataset(res);
                setAllMoneyCodes(rows);
                setOldestLoadedPeriodStart(oldestPeriodStartAmong(rows));
                setHasMoreOlder(extractHasMoreOlder(res));
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
            ac.abort();
        };
    }, []);

    const weekGroups = useMemo(
        () => groupMoneyCodesByWeek(allMoneyCodes, firstDayOfAWeek),
        [allMoneyCodes, firstDayOfAWeek],
    );

    const onLoadMore = useCallback(async () => {
        if (!hasMoreOlder || loadMoreBusy || !oldestLoadedPeriodStart || loading) return;
        setLoadMoreBusy(true);
        const ac = new AbortController();
        const qs = `?beforePeriodStart=${dateToLocalDateString(
            stripTime(oldestLoadedPeriodStart),
        )}&pageNumber=0`;
        try {
            const res = await fetchMoneyCodesResponse(qs, ac.signal);
            const rows = normalizeDataset(res);
            setHasMoreOlder(extractHasMoreOlder(res));
            setAllMoneyCodes((prev) => {
                const { merged } = mergeById(prev, rows);
                const nextOldest = oldestPeriodStartAmong(merged);
                if (nextOldest) {
                    setOldestLoadedPeriodStart(nextOldest);
                }
                return merged;
            });
        } finally {
            setLoadMoreBusy(false);
        }
    }, [hasMoreOlder, loadMoreBusy, oldestLoadedPeriodStart, loading]);

    if (error) {
        return (
            <div className="errorMessage">{error.customError ? error.customError : 'Something went wrong'}</div>
        );
    }

    if (loading) {
        return <LoadingIcon state={true} height="10rem" />;
    }

    return (
        <div className={reportStyles.mainContainer}>
            <div className={listStyles.pageHeader}>
                <div>EFS Money Code Management</div>
                <div className={listStyles.actionsContainer}>
                    <div>
                        {userCanEdit ? (
                            <div
                                className={`${listStyles.addButton} non-selectable`}
                                onClick={openIssueMoneyCode}
                            >
                                Issue Money Code +
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            <div
                className={listStyles.container}
                // eslint-disable-next-line
                style={{ ['--list-columns']: MONEY_CODE_GRID_COLUMNS }}
            >
                <div className={listStyles.listViewHeaderWrapper} style={{ gridTemplateColumns: 'var(--list-columns)' }}>
                    {displayColumns.map((col) => (
                        <div key={col.columnName} className={listStyles.listHeaderLabel}>
                            <span className={listStyles.labelText}>{col.label}</span>
                        </div>
                    ))}
                    <div className={listStyles.listHeaderLabel} />
                </div>

                {weekGroups.length === 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'var(--list-columns)', width: '100%' }}>
                        <div className={reportStyles.expensesEmptyBanner} style={{ gridColumn: '1 / -1' }}>
                            No money codes yet. Use “Load more” if you expect older records.
                        </div>
                    </div>
                ) : (
                    weekGroups.map((group) => (
                        <React.Fragment key={dateToLocalDateString(group.weekStart)}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'var(--list-columns)', width: '100%' }}>
                                <div className={reportStyles.expensesWeekDividerInner} style={{ gridColumn: '1 / -1' }}>
                                    {formatMoneyCodePeriodLabel(group)}
                                </div>
                            </div>
                            {group.moneyCodes.map((moneyCode) => (
                                <div key={moneyCode.id ?? moneyCode.efsCode} className={listStyles.row}>
                                    {displayColumns.map((col) => (
                                        <div
                                            key={col.columnName}
                                            style={
                                                col.columnName === 'description'
                                                    ? { minWidth: 0, overflow: 'hidden' }
                                                    : undefined
                                            }
                                        >
                                            {renderMoneyCodeColumnCell(col, moneyCode)}
                                        </div>
                                    ))}
                                    <ThreeDotsButton
                                        showSyncAndRemove
                                        disabled={!userCanEdit || isMoneyCodeVoid(moneyCode)}
                                        hideRemove={!canVoidMoneyCode(moneyCode)}
                                        position="relative"
                                        onEdit={() => openEditMoneyCode(moneyCode, applyMoneyCodeFromApi)}
                                        onSync={() => syncMoneyCodeFromEfs(moneyCode, applyMoneyCodeFromApi)}
                                        onRemove={() => confirmVoidMoneyCode(moneyCode, applyMoneyCodeFromApi)}
                                    />
                                </div>
                            ))}
                        </React.Fragment>
                    ))
                )}
            </div>

            {hasMoreOlder ? (
                <div className={listStyles.loadMoreButtonWrapper}>
                    <div
                        className={loadMoreBusy ? 'loading' : ''}
                        onClick={() => {
                            if (!loadMoreBusy) onLoadMore();
                        }}
                    >
                        {loadMoreBusy ? 'Loading…' : 'Load more'}
                    </div>
                </div>
            ) : (
                <div className={listStyles.loadMoreButtonWrapper}>
                    <div className={listStyles.noMoreToload}>No older money codes to load</div>
                </div>
            )}
        </div>
    );
};
